"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { authService } from "@/services/auth";
import type { LinkedInProfile } from "@/services/auth";

interface AuthContextType {
  user: LinkedInProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (userData: LinkedInProfile) => void;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<LinkedInProfile | null>(null);
  const [isLoading, setIsLoading] = useState(false); // Start with false to avoid SSR issues
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

  // Initialize auth state on mount (client-side only)
  useEffect(() => {
    // Skip during SSR/build
    if (typeof window === "undefined") return;

    const initializeAuth = async () => {
      setIsLoading(true);
      try {
        // Check if user data is stored in localStorage
        const storedUser = localStorage.getItem("linkedin_user");
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          setUser(userData);
          setIsAuthenticated(true);
          console.log("✅ User data loaded from localStorage");
        }
      } catch (error) {
        console.error("❌ Failed to initialize auth:", error);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const login = (userData: LinkedInProfile) => {
    setUser(userData);
    setIsAuthenticated(true);
    // Store user data in localStorage
    if (typeof window !== "undefined") {
      localStorage.setItem("linkedin_user", JSON.stringify(userData));
    }
  };

  const logout = () => {
    setUser(null);
    setIsAuthenticated(false);
    // Clear stored user data
    if (typeof window !== "undefined") {
      localStorage.removeItem("linkedin_user");
    }
  };

  const refreshUser = async () => {
    // For LinkedIn auth, we don't need token refresh
    // User data is stored in localStorage
    return;
  };

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
